/**
 * CRM Client detail page
 * Shows client info, communication log, and tasks
 */
import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import Modal, { ModalFooter } from '../../components/Modal'
import { formatLocalDateTime, formatLocalDate } from '../../utils/formatDateTime'
import {
  getClient,
  updateClient,
  deleteClient,
  listCommunications,
  createCommunication,
  deleteCommunication,
  listTasks,
  createTask,
  updateTask,
  deleteTask,
  getClientNotifications,
  linkLicense,
  updateClientLicense,
  unlinkClientLicense,
  listClientNotes,
  addClientNote,
  deleteClientNote,
  getClientLicenseStats,
  createQuickTask,
  checkDnc,
  getClientFccParties,
} from '../../services/crm'
import type { ClientFCCParty, ClientLicenseStatsResponse, ExpiringSoonEntry } from '../../services/crm'
import type {
  Client,
  ClientLicense,
  ClientNote,
  ClientUpdate,
  ClientPriority,
  Communication,
  CommunicationCreate,
  CommunicationType,
  CommunicationDirection,
  Task,
  TaskCreate,
  TaskUpdate,
  TaskPriority,
  TaskStatus,
  AccountStatus,
  NotificationLog,
} from '../../types'
import { listUsers } from '../../services/users'
import UserSelect from '../../components/UserSelect'
import { type UserOption } from '../../utils/userOptions'
import { XIcon } from '../../components/Icons'
import SendCardModal from '../../components/SendCardModal'
import SendChooser from '../../components/SendChooser'
import SendMessageModal from '../../components/SendMessageModal'
import ScheduledMessagesPanel from '../../components/ScheduledMessagesPanel'
import DncToggle from '../../components/DncToggle'
import EntityCard from '../../components/EntityCard'
import { StaleBadge } from './ClientsPage'
import { formatPhone, getEntityDisplayName } from '../../utils/entityDisplay'

// ---------------------------------------------------------------------------
// Helpers / constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<AccountStatus, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-600',
  prospect: 'bg-blue-100 text-blue-800',
}

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
}

const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  open: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
}

const COMM_TYPE_ICONS: Record<CommunicationType, string> = {
  call: '📞',
  email: '✉️',
  letter: '📄',
  in_person: '🤝',
  other: '💬',
}

/**
 * How many FCC parties to show before collapsing. A client whose licenses all
 * share one licensee and one filing agent — the normal case — never hits this;
 * a client holding licenses for several different entities would otherwise open
 * with a wall of cards above its own license table.
 */
const FCC_PARTIES_COLLAPSED = 4

function Badge({ label, cls }: { label: string; cls: string }) {
  return <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${cls}`}>{label}</span>
}

/**
 * One contact field in the client header.
 *
 * `fromFcc` marks a value nobody here typed — it came off the license itself. The
 * distinction matters: the client record's contact is the shop's own note of who
 * to call, and staff can edit it; the FCC value is whatever the last data update
 * carried. Showing the FCC value unlabelled would blur the two, and showing
 * nothing (which is what this page did) hid contact details we already hold.
 */
function ContactField({
  label,
  value,
  fromFcc,
  href,
}: {
  label: string
  value: string | null
  fromFcc: boolean
  href?: string
}) {
  return (
    <div>
      <span className="text-gray-500">{label}</span>
      {value ? (
        <p className={fromFcc ? 'text-gray-600' : 'text-gray-900'}>
          {href ? (
            <a href={href} className="tap text-primary-600 hover:text-primary-800">{value}</a>
          ) : (
            value
          )}
          {fromFcc && (
            <span
              className="ml-1.5 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded align-middle"
              title="From the FCC license. This client record has no contact of its own yet — use Edit to add one."
            >
              FCC
            </span>
          )}
        </p>
      ) : (
        <p className="text-gray-400">—</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const id = Number(clientId)
  // Only admins can open /licenses/ownership-changes (AdminRoute), so only they get
  // the deep link on the ownership badge below.
  const { user } = useAuth()
  const isAdmin = Boolean(user?.is_superuser)

  const [client, setClient] = useState<Client | null>(null)
  const [loadingClient, setLoadingClient] = useState(true)
  const [clientError, setClientError] = useState('')

  // Communications
  const [comms, setComms] = useState<Communication[]>([])
  const [commsTotal, setCommsTotal] = useState(0)
  const [commsPage, setCommsPage] = useState(1)
  const [loadingComms, setLoadingComms] = useState(false)
  const [showAddComm, setShowAddComm] = useState(false)
  const [commForm, setCommForm] = useState<CommunicationCreate>({
    communication_type: 'call',
    direction: 'outbound',
    subject: '',
    body: '',
    occurred_at: new Date().toISOString().slice(0, 16),
    duration_minutes: undefined,
  })
  const [commError, setCommError] = useState('')
  const [savingComm, setSavingComm] = useState(false)

  // Tasks
  const [tasks, setTasks] = useState<Task[]>([])
  const [loadingTasks, setLoadingTasks] = useState(false)
  const [showAddTask, setShowAddTask] = useState(false)
  const [taskForm, setTaskForm] = useState<TaskCreate>({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
  })
  const [taskError, setTaskError] = useState('')
  const [savingTask, setSavingTask] = useState(false)

  // Task edit
  const [editingTask, setEditingTask] = useState<Task | null>(null)
  const [editTaskForm, setEditTaskForm] = useState<TaskUpdate>({})
  const [editTaskError, setEditTaskError] = useState('')
  const [savingEditTask, setSavingEditTask] = useState(false)

  // Users for assignee picker
  // Shared by the client assignee picker and the task modals.
  const [users, setUsers] = useState<UserOption[]>([])
  const [savingAssignee, setSavingAssignee] = useState(false)
  const [assigneeError, setAssigneeError] = useState('')

  // Client notes (append-only)
  const [clientNotes, setClientNotes] = useState<ClientNote[]>([])
  const [newNote, setNewNote] = useState('')
  const [addingNote, setAddingNote] = useState(false)
  const [noteError, setNoteError] = useState('')

  // Notifications
  const [notifications, setNotifications] = useState<NotificationLog[]>([])
  const [loadingNotifs, setLoadingNotifs] = useState(false)
  // One "Send" button on the header, not one per kind of thing. It used to say
  // "Send Card" and could only do that, so the messages — including the
  // reference copy — were reachable only from a link in the Scheduled Messages
  // panel at the bottom of the page, which reads as being about scheduling.
  // Send Card is now an item in that menu rather than a separate button.
  const [showSendCard, setShowSendCard] = useState(false)
  // Send Now used to open an inline subject+body form that could only ever send
  // the Custom Message template. It is now the same dialog as everything else,
  // which is where the choice of *which* message, a signature, a preview and
  // "later" all live — one send flow rather than two half-flows.
  const [showSendMessage, setShowSendMessage] = useState(false)
  // Which message the Send menu picked, so the dialog opens on it. Null when the
  // dialog is opened without a choice, from the Scheduled Messages panel.
  const [sendTemplateId, setSendTemplateId] = useState<number | null>(null)
  const [scheduledRefresh, setScheduledRefresh] = useState(0)

  // License stats
  const [licenseStats, setLicenseStats] = useState<ClientLicenseStatsResponse | null>(null)
  const [quickTaskSuccess, setQuickTaskSuccess] = useState<Record<number, string>>({})
  const [quickTaskPending, setQuickTaskPending] = useState<Record<number, boolean>>({})
  const [statsFilter, setStatsFilter] = useState<'all' | 'managed' | 'expiring' | 'expired'>('all')

  // Do Not Contact flags for this client's call signs. DNC was previously
  // settable only from search results, so a user working inside a client record
  // had no way to flag one of its licenses.
  const [dncCallSigns, setDncCallSigns] = useState<Set<string>>(new Set())
  const [dncToast, setDncToast] = useState('')
  const [dncError, setDncError] = useState('')

  // FCC parties (licensee + filing agent) across this client's licenses. These are
  // the FCC's own contact details, not the shop's — the client record's own
  // contact fields below are typed by staff and are frequently empty, which is why
  // this page used to show three dashes for a license the search page had a full
  // contact block for.
  const [fccParties, setFccParties] = useState<ClientFCCParty[]>([])
  const [showAllFccParties, setShowAllFccParties] = useState(false)

  // Linked licenses
  const [licenses, setLicenses] = useState<ClientLicense[]>([])
  const [showLinkLicense, setShowLinkLicense] = useState(false)
  const [linkCallSign, setLinkCallSign] = useState('')
  const [linkError, setLinkError] = useState('')
  const [linking, setLinking] = useState(false)

  // Edit client
  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState<ClientUpdate>({})
  const [savingEdit, setSavingEdit] = useState(false)
  const [editError, setEditError] = useState('')

  // Delete client modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingClient, setDeletingClient] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const COMMS_PER_PAGE = 10

  // Filter linked licenses based on the active stat button
  const filteredLicenses = useMemo(() => {
    if (statsFilter === 'all') return licenses
    if (statsFilter === 'managed') return licenses.filter(l => !l.do_not_renew)
    if (statsFilter === 'expiring' && licenseStats) {
      const expiringIds = new Set(licenseStats.expiring_soon.map(e => e.license_id))
      return licenses.filter(l => expiringIds.has(l.id))
    }
    if (statsFilter === 'expired' && licenseStats) {
      const expiredIds = new Set(licenseStats.expired_license_ids)
      return licenses.filter(l => expiredIds.has(l.id))
    }
    return licenses
  }, [licenses, licenseStats, statsFilter])

  // Per-license expiration lookup, so each license shows its own status on one line.
  const expiringMap = useMemo(() => {
    const m = new Map<number, ExpiringSoonEntry>()
    licenseStats?.expiring_soon.forEach((e) => m.set(e.license_id, e))
    return m
  }, [licenseStats])
  const expiredSet = useMemo(
    () => new Set<number>(licenseStats?.expired_license_ids ?? []),
    [licenseStats],
  )

  /**
   * The FCC party to fall back on when the client record has no contact of its own.
   *
   * **The licensee, not the filing agent.** The filing agent ('CL') is the party
   * who handles renewals — but on this shop's own clients that party is *this
   * shop*: KTC939 and WNSD682 both name ACTION COMMUNICATIONS INC as the contact.
   * Filling a client's Contact line with our own switchboard number answers the
   * wrong question. The header asks "who do I call at the client", which is the
   * licensee's row. The agent is still shown in full in the FCC contacts block.
   *
   * One party feeds all three header fields, so the name, e-mail and phone shown
   * together are one person rather than three rows spliced.
   */
  const primaryFccParty = useMemo(() => {
    const licensee = fccParties.find((p) => p.entity.entity_type === 'L')
    return licensee ?? fccParties[0] ?? null
  }, [fccParties])

  // The three header values the FCC side can supply. getEntityDisplayName returns
  // 'N/A' for a party with no name at all, which is not a contact — treat it as absent.
  const fccName = useMemo(() => {
    if (!primaryFccParty) return null
    const name = getEntityDisplayName(primaryFccParty.entity)
    return name && name !== 'N/A' ? name : null
  }, [primaryFccParty])
  const fccEmail = primaryFccParty?.entity.email ?? null
  const fccPhone = primaryFccParty?.entity.phone ?? null

  useEffect(() => {
    loadClient()
    loadTasks()
    loadNotifications()
    loadClientNotes()
    fetchLicenseStats()
    loadFccParties()
    listUsers().then((res) => setUsers(res.users)).catch(() => {})
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadComms()
  }, [id, commsPage]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Look up which of this client's call signs are on the Do Not Contact list.
   *
   * One request per call sign (the check endpoint is per-value and rate-limited
   * at 120/min). Display-only: a failure leaves the row unflagged rather than
   * breaking the page.
   */
  async function loadDncFlags(clientLicenses: ClientLicense[]) {
    const callSigns = Array.from(
      new Set(clientLicenses.map((l) => l.call_sign).filter((c): c is string => !!c))
    )
    if (callSigns.length === 0) {
      setDncCallSigns(new Set())
      return
    }
    const results = await Promise.allSettled(
      callSigns.map((cs) => checkDnc('call_sign', cs).then((r) => ({ cs, isDnc: r.is_dnc })))
    )
    setDncCallSigns(
      new Set(
        results
          .filter((r): r is PromiseFulfilledResult<{ cs: string; isDnc: boolean }> =>
            r.status === 'fulfilled' && r.value.isDnc
          )
          .map((r) => r.value.cs)
      )
    )
  }

  /** Reflect a DncToggle result in the set of flagged call signs. */
  function applyDncChange(callSign: string, isDnc: boolean) {
    setDncCallSigns((prev) => {
      const s = new Set(prev)
      if (isDnc) s.add(callSign)
      else s.delete(callSign)
      return s
    })
  }

  async function loadClient() {
    setLoadingClient(true)
    setClientError('')
    try {
      const c = await getClient(id)
      setClient(c)
      setLicenses(c.licenses)
      loadDncFlags(c.licenses)
      setEditForm({
        entity_name: c.entity_name,
        contact_name: c.contact_name || '',
        contact_email: c.contact_email || '',
        contact_phone: c.contact_phone || '',
        account_status: c.account_status,
        priority: c.priority,
        notes: c.notes || '',
      })
    } catch (e) {
      setClientError(e instanceof Error ? e.message : 'Failed to load client')
    } finally {
      setLoadingClient(false)
    }
  }

  async function loadComms() {
    setLoadingComms(true)
    try {
      const res = await listCommunications(id, { page: commsPage, page_size: COMMS_PER_PAGE })
      setComms(res.items)
      setCommsTotal(res.total)
    } catch {
      // non-critical
    } finally {
      setLoadingComms(false)
    }
  }

  async function loadTasks() {
    setLoadingTasks(true)
    try {
      const res = await listTasks({ client_id: id, page_size: 50 })
      setTasks(res.items)
    } catch {
      // non-critical
    } finally {
      setLoadingTasks(false)
    }
  }

  async function loadClientNotes() {
    try {
      const notes = await listClientNotes(id)
      setClientNotes(notes)
    } catch { /* non-critical */ }
  }

  async function handleAddClientNote() {
    if (!newNote.trim()) return
    setAddingNote(true)
    setNoteError('')
    try {
      const note = await addClientNote(id, newNote.trim())
      setClientNotes([...clientNotes, note])
      setNewNote('')
    } catch (e) {
      setNoteError(e instanceof Error ? e.message : 'Failed to add note')
    } finally {
      setAddingNote(false)
    }
  }

  async function handleDeleteClientNote(noteId: number) {
    try {
      await deleteClientNote(id, noteId)
      setClientNotes(clientNotes.filter((n) => n.id !== noteId))
    } catch { /* ignore */ }
  }

  async function loadNotifications() {
    setLoadingNotifs(true)
    try {
      const logs = await getClientNotifications(id)
      setNotifications(logs)
    } catch {
      // non-critical
    } finally {
      setLoadingNotifs(false)
    }
  }

  async function fetchLicenseStats() {
    try {
      const result = await getClientLicenseStats(id)
      setLicenseStats(result)
    } catch {
      // non-critical — stats panel just won't show
    }
  }

  async function loadFccParties() {
    try {
      setFccParties(await getClientFccParties(id))
    } catch {
      // Display-only: a failure leaves the FCC block out rather than breaking the page.
      setFccParties([])
    }
  }

  async function handleQuickTask(entry: ExpiringSoonEntry, template: 'renew' | 'contact_first') {
    const licId = entry.license_id
    setQuickTaskPending((prev) => ({ ...prev, [licId]: true }))
    try {
      await createQuickTask(id, {
        call_sign: entry.call_sign || '',
        expiration_date: entry.expiration_date,
        template,
      })
      setQuickTaskSuccess((prev) => ({ ...prev, [licId]: 'Task created' }))
      loadTasks()
    } catch (e) {
      setQuickTaskSuccess((prev) => ({
        ...prev,
        [licId]: e instanceof Error ? e.message : 'Failed to create task',
      }))
    } finally {
      setQuickTaskPending((prev) => ({ ...prev, [licId]: false }))
    }
  }

  async function handleLinkLicense(e: React.FormEvent) {
    e.preventDefault()
    const callSign = linkCallSign.trim().toUpperCase()
    if (!callSign) return
    setLinking(true)
    setLinkError('')
    try {
      // Backend expects fcc_unique_system_identifier — look it up via the search API
      // We use the call_sign to find the USI via a quick license lookup
      const resp = await import('../../services/licenses').then(m => m.searchLicenses({ call_sign: callSign, limit: 1 }))
      const lic = resp.licenses[0]
      if (!lic) { setLinkError('Call sign not found'); setLinking(false); return }
      const linked = await linkLicense(id, lic.unique_system_identifier)
      setLicenses([...licenses, linked])
      setLinkCallSign('')
      setShowLinkLicense(false)
      loadFccParties()  // the new license may bring a party the client didn't have
    } catch (e) {
      setLinkError(e instanceof Error ? e.message : 'Failed to link')
    } finally {
      setLinking(false)
    }
  }

  async function handleToggleNotify(lic: ClientLicense) {
    try {
      const updated = await updateClientLicense(lic.id, { notify_enabled: !lic.notify_enabled })
      setLicenses(licenses.map(l => l.id === updated.id ? updated : l))
    } catch { /* ignore */ }
  }

  async function handleToggleDoNotRenew(lic: ClientLicense) {
    const next = !lic.do_not_renew
    if (next && !confirm(
      `Mark ${lic.call_sign || 'this license'} as "Do Not Renew"?\n\n` +
      'All renewal notifications (client and staff) will be suppressed and this license will be excluded from the lead pipeline.'
    )) return
    try {
      const updated = await updateClientLicense(lic.id, { do_not_renew: next })
      setLicenses(licenses.map(l => l.id === updated.id ? updated : l))
    } catch { /* ignore */ }
  }

  async function handleUnlinkLicense(licId: number) {
    if (!confirm('Unlink this license from the client?')) return
    try {
      await unlinkClientLicense(licId)
      setLicenses(licenses.filter(l => l.id !== licId))
      loadFccParties()  // that license's parties may no longer belong to this client
    } catch { /* ignore */ }
  }

  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault()
    setSavingEdit(true)
    setEditError('')
    try {
      const updated = await updateClient(id, editForm)
      setClient(updated)
      setEditMode(false)
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to save')
    } finally {
      setSavingEdit(false)
    }
  }

  /** Re-assign from the header — saves immediately, no edit mode needed. */
  async function handleAssigneeChange(userId: number | null) {
    setSavingAssignee(true)
    setAssigneeError('')
    try {
      // null (not undefined) is what clears the assignee server-side.
      const updated = await updateClient(id, { assigned_to_user_id: userId })
      setClient(updated)
    } catch (e) {
      setAssigneeError(e instanceof Error ? e.message : 'Failed to re-assign')
    } finally {
      setSavingAssignee(false)
    }
  }

  async function handleDeleteClient() {
    setDeletingClient(true)
    setDeleteError('')
    try {
      await deleteClient(id)
      navigate('/crm/clients')
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete')
      setDeletingClient(false)
    }
  }

  async function handleAddComm(e: React.FormEvent) {
    e.preventDefault()
    setSavingComm(true)
    setCommError('')
    try {
      const payload: CommunicationCreate = {
        communication_type: commForm.communication_type,
        direction: commForm.direction,
        occurred_at: new Date(commForm.occurred_at).toISOString(),
      }
      if (commForm.subject?.trim()) payload.subject = commForm.subject.trim()
      if (commForm.body?.trim()) payload.body = commForm.body.trim()
      if (commForm.duration_minutes) payload.duration_minutes = commForm.duration_minutes
      await createCommunication(id, payload)
      setShowAddComm(false)
      setCommForm({
        communication_type: 'call',
        direction: 'outbound',
        subject: '',
        body: '',
        occurred_at: new Date().toISOString().slice(0, 16),
      })
      setCommsPage(1)
      loadComms()
    } catch (e) {
      setCommError(e instanceof Error ? e.message : 'Failed to log communication')
    } finally {
      setSavingComm(false)
    }
  }

  async function handleDeleteComm(commId: number) {
    if (!confirm('Delete this communication entry?')) return
    try {
      await deleteCommunication(commId)
      loadComms()
    } catch { /* ignore */ }
  }

  async function handleAddTask(e: React.FormEvent) {
    e.preventDefault()
    if (!taskForm.title.trim()) { setTaskError('Title is required'); return }
    setSavingTask(true)
    setTaskError('')
    try {
      const payload: TaskCreate = {
        title: taskForm.title.trim(),
        priority: taskForm.priority,
        client_id: id,
      }
      if (taskForm.description?.trim()) payload.description = taskForm.description.trim()
      if (taskForm.due_date) payload.due_date = taskForm.due_date
      await createTask(payload)
      setShowAddTask(false)
      setTaskForm({ title: '', description: '', due_date: '', priority: 'medium' })
      loadTasks()
    } catch (e) {
      setTaskError(e instanceof Error ? e.message : 'Failed to create task')
    } finally {
      setSavingTask(false)
    }
  }

  async function handleSaveEditTask(e: React.FormEvent) {
    e.preventDefault()
    if (!editingTask) return
    setSavingEditTask(true)
    setEditTaskError('')
    try {
      // editTaskForm.due_at is stored as datetime-local (no seconds); add seconds for ISO compliance
      const payload: TaskUpdate = { ...editTaskForm }
      if (payload.due_at && payload.due_at.length === 16) {
        payload.due_at = payload.due_at + ':00'
        payload.due_date = payload.due_at.slice(0, 10)
      }
      await updateTask(editingTask.id, payload)
      setEditingTask(null)
      loadTasks()
    } catch (e) {
      setEditTaskError(e instanceof Error ? e.message : 'Failed to update task')
    } finally {
      setSavingEditTask(false)
    }
  }

  async function handleCompleteTask(task: Task) {
    try {
      await updateTask(task.id, { status: 'completed' })
      loadTasks()
    } catch { /* ignore */ }
  }

  async function handleDeleteTask(taskId: number) {
    if (!confirm('Delete this task?')) return
    try {
      await deleteTask(taskId)
      loadTasks()
    } catch { /* ignore */ }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loadingClient) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (clientError || !client) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md">
        {clientError || 'Client not found'}
        <button onClick={() => navigate('/crm/clients')} className="ml-4 text-sm underline">
          Back to clients
        </button>
      </div>
    )
  }

  const openTasks = tasks.filter((t) => t.status === 'open' || t.status === 'in_progress')
  const doneTasks = tasks.filter((t) => t.status === 'completed' || t.status === 'cancelled')
  const commsPages = Math.ceil(commsTotal / COMMS_PER_PAGE)

  return (
    <>
    <div>
      {/* Do Not Contact feedback (same presentation as search results) */}
      {dncToast && (
        <div className="fixed bottom-4 right-4 z-50 bg-gray-800 text-white text-sm px-4 py-2.5 rounded shadow-lg" role="status">
          {dncToast}
        </div>
      )}
      {dncError && (
        <div className="alert alert-error text-sm mb-4" role="alert">
          {dncError}
          <button className="ml-2 underline" onClick={() => setDncError('')}>dismiss</button>
        </div>
      )}

      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 mb-4">
        <Link to="/crm/clients" className="tap hover:underline">Clients</Link>
        {' / '}
        <span className="text-gray-900">{client.entity_name}</span>
      </nav>

      {/* Client Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        {!editMode ? (
          <>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-gray-900">{client.entity_name}</h1>
                  <Badge
                    label={client.account_status.charAt(0).toUpperCase() + client.account_status.slice(1)}
                    cls={STATUS_COLORS[client.account_status]}
                  />
                  <Badge
                    label={client.priority.charAt(0).toUpperCase() + client.priority.slice(1)}
                    cls={PRIORITY_COLORS[client.priority as TaskPriority]}
                  />
                  {client.is_stale && <StaleBadge />}
                </div>
                {licenses.length > 0 && (
                  <p className="text-gray-500 text-sm mt-1 font-mono">
                    {licenses.map(l => l.call_sign).filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <div className="toolbar gap-2">
                <SendChooser
                  onChooseCard={() => setShowSendCard(true)}
                  onChooseTemplate={(templateId) => {
                    setSendTemplateId(templateId)
                    setShowSendMessage(true)
                  }}
                />
                <button
                  onClick={() => setEditMode(true)}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Edit
                </button>
                <button
                  onClick={() => { setDeleteError(''); setShowDeleteModal(true) }}
                  className="px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <ContactField
                label="Contact"
                value={client.contact_name || fccName}
                fromFcc={!client.contact_name && !!fccName}
              />
              <ContactField
                label="Email"
                value={client.contact_email || fccEmail}
                fromFcc={!client.contact_email && !!fccEmail}
                href={
                  client.contact_email
                    ? `mailto:${client.contact_email}`
                    : fccEmail
                      ? `mailto:${fccEmail}`
                      : undefined
                }
              />
              <ContactField
                label="Phone"
                value={client.contact_phone || (fccPhone ? formatPhone(fccPhone) : null)}
                fromFcc={!client.contact_phone && !!fccPhone}
                href={
                  client.contact_phone
                    ? `tel:${client.contact_phone}`
                    : fccPhone
                      ? `tel:${fccPhone}`
                      : undefined
                }
              />
              <div>
                <span className="text-gray-500">Assigned To</span>
                {/* Re-assign in place — the same edit rights the Edit button needs,
                    enforced by the API (a viewer gets the error inline). */}
                <UserSelect
                  users={users}
                  value={client.assigned_to_user_id}
                  onChange={handleAssigneeChange}
                  disabled={savingAssignee}
                  ariaLabel="Assigned to"
                  className="mt-0.5 w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
                />
                {assigneeError && (
                  <p className="text-xs text-red-600 mt-0.5">{assigneeError}</p>
                )}
              </div>
            </div>

            {/* Append-only notes */}
            {clientNotes.length > 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded">
                <span className="font-medium text-gray-500 text-xs uppercase block mb-2">Notes</span>
                <div className="space-y-2">
                  {clientNotes.map((n) => (
                    <div key={n.id} className="flex gap-2 items-start group">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.note}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {formatLocalDateTime(n.created_at)}
                          {n.created_by && (
                            <span className="text-gray-300"> · {n.created_by.full_name ?? n.created_by.email}</span>
                          )}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteClientNote(n.id)}
                        className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 rounded"
                        title="Delete note"
                        aria-label="Delete note"
                      >
                        <XIcon />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Note */}
            <div className="mt-4 border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Add Note
                </span>
              </div>
              {noteError && <p className="text-xs text-red-600 mb-1">{noteError}</p>}
              <div className="flex gap-2 items-end">
                <textarea
                  rows={2}
                  className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  placeholder="Add a note…"
                  maxLength={5000}
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
                <button
                  type="button"
                  onClick={handleAddClientNote}
                  disabled={addingNote || !newNote.trim()}
                  className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  {addingNote ? '…' : 'Add'}
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Edit form */
          <form onSubmit={handleSaveEdit} className="space-y-3">
            <h2 className="text-lg font-semibold">Edit Client</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Entity Name *</label>
              <input
                type="text"
                value={editForm.entity_name || ''}
                onChange={(e) => setEditForm({ ...editForm, entity_name: e.target.value })}
                required
                maxLength={255}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editForm.account_status || 'active'}
                  onChange={(e) => setEditForm({ ...editForm, account_status: e.target.value as AccountStatus })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {/* `prospect` is retired — this field is intent only. A legacy row
                      keeps its badge; it just cannot be chosen again. */}
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={editForm.priority || 'medium'}
                  onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as ClientPriority })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
              <input
                type="text"
                value={editForm.contact_name || ''}
                onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                maxLength={100}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.contact_email || ''}
                  onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={editForm.contact_phone || ''}
                  onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                  maxLength={30}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
            {editError && <p className="text-sm text-red-600">{editError}</p>}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={savingEdit}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {savingEdit ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setEditMode(false)}
                className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* FCC contact details — the licensee and the filing agent on this client's
          licenses. Read-only: these come from the FCC data, and an edit here would
          be overwritten by the next update. */}
      {fccParties.length > 0 && (
        <div className="mb-6 bg-white rounded-lg shadow">
          <div className="px-5 py-4 border-b">
            <h2 className="text-base font-semibold text-gray-900">
              Contacts on file with the FCC
              {fccParties.length > 1 && (
                <span className="ml-2 text-sm font-normal text-gray-500">({fccParties.length})</span>
              )}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              From this client's licenses. The contact is the person or firm that files with the
              FCC on the licensee's behalf.
            </p>
          </div>
          <div className="px-5 py-4 space-y-3">
            {(showAllFccParties ? fccParties : fccParties.slice(0, FCC_PARTIES_COLLAPSED)).map(
              (party, idx) => (
                <EntityCard
                  key={`${party.entity.id}-${idx}`}
                  entity={party.entity}
                  isPrimary={idx === 0}
                  callSigns={licenses.length > 1 ? party.call_signs : undefined}
                />
              )
            )}
            {fccParties.length > FCC_PARTIES_COLLAPSED && (
              <button
                type="button"
                onClick={() => setShowAllFccParties(!showAllFccParties)}
                className="tap text-sm text-primary-600 hover:underline"
              >
                {showAllFccParties
                  ? 'Show fewer'
                  : `Show all ${fccParties.length} contacts`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Licenses section — stat filter buttons + linked license table (merged) */}
      <div className="mb-6 bg-white rounded-lg shadow">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-base font-semibold text-gray-900">
            Licenses
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({filteredLicenses.length}{statsFilter !== 'all' ? ` of ${licenses.length}` : ''})
            </span>
          </h2>
          <button
            onClick={() => { setShowLinkLicense(!showLinkLicense); setLinkError(''); setLinkCallSign('') }}
            className="tap text-sm text-primary-600 hover:underline"
          >
            {showLinkLicense ? 'Cancel' : '+ Link License'}
          </button>
        </div>

        <div className="px-5 py-4">
          {/* Stat filter buttons */}
          {licenseStats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <button
                onClick={() => setStatsFilter('all')}
                className={`text-center p-3 rounded-lg border-2 transition-colors ${
                  statsFilter === 'all'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="text-2xl font-bold text-gray-900">{licenseStats.stats.total_licenses}</div>
                <div className="text-xs text-gray-500 mt-1">Total</div>
              </button>
              <button
                onClick={() => setStatsFilter(statsFilter === 'managed' ? 'all' : 'managed')}
                className={`text-center p-3 rounded-lg border-2 transition-colors ${
                  statsFilter === 'managed'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="text-2xl font-bold text-blue-600">{licenseStats.stats.managed_licenses}</div>
                <div className="text-xs text-gray-500 mt-1">Managed</div>
              </button>
              <button
                onClick={() => setStatsFilter(statsFilter === 'expiring' ? 'all' : 'expiring')}
                className={`text-center p-3 rounded-lg border-2 transition-colors ${
                  statsFilter === 'expiring'
                    ? 'border-orange-400 bg-orange-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className={`text-2xl font-bold ${licenseStats.stats.expiring_90_days > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                  {licenseStats.stats.expiring_90_days}
                </div>
                <div className="text-xs text-gray-500 mt-1">Expiring (90d)</div>
              </button>
              <button
                onClick={() => setStatsFilter(statsFilter === 'expired' ? 'all' : 'expired')}
                className={`text-center p-3 rounded-lg border-2 transition-colors ${
                  statsFilter === 'expired'
                    ? 'border-red-400 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className={`text-2xl font-bold ${licenseStats.stats.expired > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  {licenseStats.stats.expired}
                </div>
                <div className="text-xs text-gray-500 mt-1">Expired</div>
              </button>
            </div>
          )}

          {/* Link form */}
          {showLinkLicense && (
            <form onSubmit={handleLinkLicense} className="flex gap-2 mb-4">
              <input
                type="text"
                value={linkCallSign}
                onChange={(e) => setLinkCallSign(e.target.value.toUpperCase())}
                placeholder="Call sign (e.g. WQHJ123)"
                maxLength={10}
                className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <button
                type="submit"
                disabled={linking || !linkCallSign.trim()}
                className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              >
                {linking ? '…' : 'Link'}
              </button>
              {linkError && <span className="text-xs text-red-600 self-center">{linkError}</span>}
            </form>
          )}

          {/* License table */}
          {filteredLicenses.length === 0 ? (
            <p className="text-sm text-gray-400">
              {licenses.length === 0
                ? 'No licenses linked yet.'
                : `No licenses match the "${statsFilter}" filter.`}
            </p>
          ) : (
            <div className="table-wrap">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b">
                    <th className="text-left pb-1">Call Sign</th>
                    <th className="text-left pb-1">Expires</th>
                    <th className="text-left pb-1 hidden sm:table-cell">USI</th>
                    <th className="text-left pb-1">Notify</th>
                    <th className="text-left pb-1">Do Not Renew</th>
                    <th className="text-left pb-1" title="Do Not Contact — suppresses outreach to this call sign">Do Not Contact</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredLicenses.map((lic) => (
                    <tr key={lic.id} className={`border-b border-gray-50 ${lic.do_not_renew ? 'bg-gray-50 opacity-75' : ''}`}>
                      <td className="py-1 font-mono font-medium">
                        {lic.call_sign ? (
                          <>
                            <Link
                              to={`/licenses/${lic.call_sign}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:underline"
                              title="Open full license detail in a new tab (keeps your notes here)"
                            >
                              {lic.call_sign}
                            </Link>
                            <a
                              href={`/licenses/${lic.call_sign}/reference`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-xs font-sans text-gray-400 hover:text-primary-600 hover:underline"
                              title="Open the FCC Reference Copy in a new tab"
                            >
                              Reference Copy ↗
                            </a>
                          </>
                        ) : (
                          <span className="text-gray-900">—</span>
                        )}
                        {lic.do_not_renew && (
                          <span className="ml-2 text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded font-sans">no renew</span>
                        )}
                        {!lic.fcc_data_current && (
                          <span
                            className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-sans"
                            title={
                              'This license is no longer in the current FCC data. Details shown are the ' +
                              'last known values' +
                              (lic.snapshot_updated_at
                                ? ` (as of ${formatLocalDate(lic.snapshot_updated_at)})`
                                : '') +
                              '.'
                            }
                          >
                            FCC archived
                          </span>
                        )}
                        {lic.ownership_change_id !== null && (
                          // Only admins can open the change log, so only they get the
                          // link. A badge that sends everyone else to a page they get
                          // bounced from is worse than a badge that just informs.
                          isAdmin ? (
                            <Link
                              to="/licenses/ownership-changes"
                              onClick={(e) => e.stopPropagation()}
                              className="ml-2 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-sans font-semibold hover:bg-amber-200"
                              title="The FCC entity name or FRN on this license changed and has not been reviewed"
                            >
                              Ownership changed ↗
                            </Link>
                          ) : (
                            <span
                              className="ml-2 text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-sans font-semibold"
                              title="The FCC entity name or FRN on this license changed and has not been reviewed"
                            >
                              Ownership changed
                            </span>
                          )
                        )}
                        {!lic.fcc_data_current && lic.entity_name && (
                          <span className="block text-xs font-sans text-gray-400">{lic.entity_name}</span>
                        )}
                      </td>
                      <td className="py-1 text-gray-600 whitespace-nowrap">
                        {(() => {
                          const exp = expiringMap.get(lic.id)
                          if (exp) {
                            const daysLeft = Math.ceil(
                              (new Date(exp.expiration_date).getTime() - Date.now()) / 86400000
                            )
                            return (
                              <span>
                                {exp.expiration_date}
                                <span className={`ml-1.5 text-xs ${daysLeft <= 30 ? 'text-red-600 font-medium' : 'text-orange-500'}`}>
                                  ({daysLeft}d)
                                </span>
                              </span>
                            )
                          }
                          if (expiredSet.has(lic.id)) return <span className="text-red-600 font-medium">Expired</span>
                          // Live FCC data gone: fall back to the frozen snapshot expiry.
                          if (!lic.fcc_data_current && lic.expired_date) {
                            return <span className="text-gray-500" title="Last known FCC expiry">{lic.expired_date}</span>
                          }
                          return <span className="text-gray-400">—</span>
                        })()}
                      </td>
                      <td className="py-1 text-gray-500 hidden sm:table-cell">{lic.fcc_unique_system_identifier}</td>
                      <td className="py-1">
                        <button
                          type="button"
                          onClick={() => handleToggleNotify(lic)}
                          disabled={lic.do_not_renew}
                          title={
                            lic.do_not_renew
                              ? 'Notifications suppressed by Do Not Renew flag'
                              : lic.notify_enabled
                                ? 'Notifications enabled — click to disable'
                                : 'Notifications disabled — click to enable'
                          }
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            lic.do_not_renew
                              ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                              : lic.notify_enabled
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-400'
                          }`}
                        >
                          {lic.notify_enabled && !lic.do_not_renew ? 'On' : 'Off'}
                        </button>
                      </td>
                      <td className="py-1">
                        <button
                          type="button"
                          onClick={() => handleToggleDoNotRenew(lic)}
                          title={
                            lic.do_not_renew
                              ? 'Do Not Renew is set — click to clear (resume notifications)'
                              : 'Click to mark this license as Do Not Renew (suppresses all notifications)'
                          }
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            lic.do_not_renew
                              ? 'bg-red-100 text-red-700 font-semibold'
                              : 'bg-gray-100 text-gray-400 hover:bg-orange-50 hover:text-orange-600'
                          }`}
                        >
                          {lic.do_not_renew ? 'Yes' : 'No'}
                        </button>
                      </td>
                      <td className="py-1">
                        {lic.call_sign ? (
                          <DncToggle
                            callSign={lic.call_sign}
                            isDnc={dncCallSigns.has(lic.call_sign)}
                            onChange={(isDnc) => applyDncChange(lic.call_sign!, isDnc)}
                            onMessage={(msg) => {
                              setDncToast(msg)
                              setTimeout(() => setDncToast(''), 3000)
                            }}
                            onError={setDncError}
                          />
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="py-1 text-right whitespace-nowrap">
                        {(() => {
                          const exp = expiringMap.get(lic.id)
                          if (!exp) return null
                          const isPending = quickTaskPending[lic.id]
                          const successMsg = quickTaskSuccess[lic.id]
                          if (successMsg) {
                            return (
                              <span className={`mr-2 text-xs px-2 py-1 rounded ${
                                successMsg === 'Task created' ? 'text-green-700 bg-green-50' : 'text-red-600 bg-red-50'
                              }`}>
                                {successMsg}
                              </span>
                            )
                          }
                          return (
                            <div className="relative inline-block group mr-1 align-middle">
                              <button
                                disabled={isPending}
                                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1"
                              >
                                {isPending ? 'Creating…' : 'Create Task'}
                                <span className="text-gray-400">▾</span>
                              </button>
                              <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded shadow-lg z-10 hidden group-hover:block">
                                <button
                                  onClick={() => handleQuickTask(exp, 'renew')}
                                  disabled={isPending}
                                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 border-b"
                                >
                                  Renew license
                                </button>
                                <button
                                  onClick={() => handleQuickTask(exp, 'contact_first')}
                                  disabled={isPending}
                                  className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                >
                                  Contact client first
                                </button>
                              </div>
                            </div>
                          )
                        })()}
                        <button
                          type="button"
                          onClick={() => handleUnlinkLicense(lic.id)}
                          className="p-1 text-red-400 hover:text-red-600 rounded align-middle"
                          title="Unlink license"
                          aria-label="Unlink license"
                        >
                          <XIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Two-column layout: Communications + Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ---- Communications ---- */}
        <div className="bg-white rounded-lg shadow">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h2 className="text-base font-semibold text-gray-900">
              Communication Log
              <span className="ml-2 text-xs font-normal text-gray-500">({commsTotal})</span>
            </h2>
            <button
              onClick={() => { setShowAddComm(!showAddComm); setCommError('') }}
              className="tap text-sm text-primary-600 hover:underline"
            >
              {showAddComm ? 'Cancel' : '+ Add'}
            </button>
          </div>

          {showAddComm && (
            <form onSubmit={handleAddComm} className="px-5 py-4 bg-gray-50 border-b space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                  <select
                    value={commForm.communication_type}
                    onChange={(e) => setCommForm({ ...commForm, communication_type: e.target.value as CommunicationType })}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="call">Call</option>
                    <option value="email">Email</option>
                    <option value="letter">Letter</option>
                    <option value="in_person">In Person</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Direction</label>
                  <select
                    value={commForm.direction}
                    onChange={(e) => setCommForm({ ...commForm, direction: e.target.value as CommunicationDirection })}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="outbound">Outbound</option>
                    <option value="inbound">Inbound</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date/Time</label>
                  <input
                    type="datetime-local"
                    value={commForm.occurred_at}
                    onChange={(e) => setCommForm({ ...commForm, occurred_at: e.target.value })}
                    required
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Duration (min)</label>
                  <input
                    type="number"
                    min={0}
                    max={1440}
                    value={commForm.duration_minutes ?? ''}
                    onChange={(e) => setCommForm({ ...commForm, duration_minutes: e.target.value ? Number(e.target.value) : undefined })}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
                <input
                  type="text"
                  value={commForm.subject}
                  onChange={(e) => setCommForm({ ...commForm, subject: e.target.value })}
                  maxLength={200}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea
                  value={commForm.body}
                  onChange={(e) => setCommForm({ ...commForm, body: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              {commError && <p className="text-sm text-red-600">{commError}</p>}
              <button
                type="submit"
                disabled={savingComm}
                className="w-full py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {savingComm ? 'Logging...' : 'Log Communication'}
              </button>
            </form>
          )}

          <div className="divide-y divide-gray-100">
            {loadingComms ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
              </div>
            ) : comms.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-6">No communications logged yet.</p>
            ) : (
              comms.map((c) => (
                <div key={c.id} className="px-5 py-3 group">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span title={c.communication_type}>{COMM_TYPE_ICONS[c.communication_type]}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {c.subject || c.communication_type.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {c.direction === 'outbound' ? '↑ Outbound' : '↓ Inbound'} ·{' '}
                          {formatLocalDateTime(c.occurred_at)}
                          {c.duration_minutes ? ` · ${c.duration_minutes}m` : ''}
                          {c.logged_by ? ` · ${c.logged_by.full_name || c.logged_by.email}` : ''}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteComm(c.id)}
                      className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity ml-2 rounded"
                      title="Delete communication"
                      aria-label="Delete communication"
                    >
                      <XIcon />
                    </button>
                  </div>
                  {c.body && (
                    <p className="mt-1 text-sm text-gray-600 ml-6 whitespace-pre-wrap">{c.body}</p>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Comm pagination */}
          {commsPages > 1 && (
            <div className="flex justify-between items-center px-5 py-3 border-t text-sm text-gray-500">
              <span>Page {commsPage} of {commsPages}</span>
              <div className="flex gap-2">
                <button onClick={() => setCommsPage(commsPage - 1)} disabled={commsPage === 1}
                  aria-label="Previous page"
                  className="px-3 py-2 border rounded disabled:opacity-40 hover:bg-gray-50 min-w-[2.5rem] text-center">‹</button>
                <button onClick={() => setCommsPage(commsPage + 1)} disabled={commsPage === commsPages}
                  aria-label="Next page"
                  className="px-3 py-2 border rounded disabled:opacity-40 hover:bg-gray-50 min-w-[2.5rem] text-center">›</button>
              </div>
            </div>
          )}
        </div>

        {/* ---- Tasks ---- */}
        <div className="bg-white rounded-lg shadow">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h2 className="text-base font-semibold text-gray-900">
              Tasks
              {openTasks.length > 0 && (
                <span className="ml-2 text-xs font-normal text-orange-600">({openTasks.length} open)</span>
              )}
            </h2>
            <button
              onClick={() => { setShowAddTask(!showAddTask); setTaskError('') }}
              className="tap text-sm text-primary-600 hover:underline"
            >
              {showAddTask ? 'Cancel' : '+ Add'}
            </button>
          </div>

          {showAddTask && (
            <form onSubmit={handleAddTask} className="px-5 py-4 bg-gray-50 border-b space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  required
                  maxLength={200}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Due Date & Time</label>
                  <input
                    type="datetime-local"
                    value={taskForm.due_at || ''}
                    onChange={(e) => setTaskForm({ ...taskForm, due_at: e.target.value, due_date: e.target.value.slice(0, 10) })}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value as TaskPriority })}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Assigned To</label>
                <select
                  value={taskForm.assigned_to_user_id ?? ''}
                  onChange={(e) => setTaskForm({ ...taskForm, assigned_to_user_id: e.target.value ? parseInt(e.target.value) : undefined })}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">Unassigned</option>
                  {users.map((u) => (
                    <option key={u.id} value={String(u.id)}>{u.full_name || u.email}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
              {taskError && <p className="text-sm text-red-600">{taskError}</p>}
              <button
                type="submit"
                disabled={savingTask}
                className="w-full py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {savingTask ? 'Creating...' : 'Create Task'}
              </button>
            </form>
          )}

          <div className="divide-y divide-gray-100">
            {loadingTasks ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
              </div>
            ) : tasks.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-6">No tasks yet.</p>
            ) : (
              <>
                {openTasks.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    priorityColors={PRIORITY_COLORS}
                    statusColors={TASK_STATUS_COLORS}
                    onComplete={() => handleCompleteTask(t)}
                    onDelete={() => handleDeleteTask(t.id)}
                    onEdit={() => {
                      setEditingTask(t)
                      setEditTaskForm({
                        title: t.title,
                        description: t.description || '',
                        due_at: t.due_at ? t.due_at.slice(0, 16) : (t.due_date ? t.due_date + 'T00:00' : ''),
                        priority: t.priority,
                        status: t.status,
                        assigned_to_user_id: t.assigned_to_user_id ?? undefined,
                      })
                      setEditTaskError('')
                    }}
                  />
                ))}
                {doneTasks.length > 0 && openTasks.length > 0 && (
                  <div className="px-5 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase">
                    Completed / Cancelled
                  </div>
                )}
                {doneTasks.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    priorityColors={PRIORITY_COLORS}
                    statusColors={TASK_STATUS_COLORS}
                    onComplete={() => {}}
                    onDelete={() => handleDeleteTask(t.id)}
                    onEdit={() => {
                      setEditingTask(t)
                      setEditTaskForm({
                        title: t.title,
                        description: t.description || '',
                        due_at: t.due_at ? t.due_at.slice(0, 16) : (t.due_date ? t.due_date + 'T00:00' : ''),
                        priority: t.priority,
                        status: t.status,
                        assigned_to_user_id: t.assigned_to_user_id ?? undefined,
                      })
                      setEditTaskError('')
                    }}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete client modal */}
      {showDeleteModal && (
        <Modal
          titleId="delete-client-title"
          title="Delete Client"
          onClose={() => setShowDeleteModal(false)}
          maxWidth="max-w-sm"
        >
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to delete <strong>{client.entity_name}</strong>? This cannot be undone.
          </p>
          {deleteError && <div className="alert alert-error mb-4" role="alert">{deleteError}</div>}
          <ModalFooter>
            <button type="button" onClick={() => setShowDeleteModal(false)} className="btn btn-secondary">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteClient}
              disabled={deletingClient}
              className="btn bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deletingClient ? 'Deleting…' : 'Delete'}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <Modal
          titleId="edit-task-title"
          title="Edit Task"
          onClose={() => setEditingTask(null)}
          maxWidth="max-w-md"
        >
          <form onSubmit={handleSaveEditTask} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={editTaskForm.title || ''}
              onChange={(e) => setEditTaskForm({ ...editTaskForm, title: e.target.value })}
              required
              maxLength={200}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date & Time</label>
              <input
                type="datetime-local"
                value={typeof editTaskForm.due_at === 'string' ? editTaskForm.due_at.slice(0, 16) : ''}
                onChange={(e) => setEditTaskForm({ ...editTaskForm, due_at: e.target.value || undefined })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={editTaskForm.priority || 'medium'}
                onChange={(e) => setEditTaskForm({ ...editTaskForm, priority: e.target.value as TaskPriority })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={editTaskForm.status || 'open'}
                onChange={(e) => setEditTaskForm({ ...editTaskForm, status: e.target.value as TaskStatus })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
              <select
                value={editTaskForm.assigned_to_user_id ? String(editTaskForm.assigned_to_user_id) : ''}
                onChange={(e) => setEditTaskForm({ ...editTaskForm, assigned_to_user_id: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Unassigned</option>
                {users.map((u) => (
                  <option key={u.id} value={String(u.id)}>{u.full_name || u.email}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={editTaskForm.description || ''}
              onChange={(e) => setEditTaskForm({ ...editTaskForm, description: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          {editTaskError && <p className="text-sm text-red-600">{editTaskError}</p>}
          <ModalFooter>
            <button type="button" onClick={() => setEditingTask(null)} className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={savingEditTask} className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50">
              {savingEditTask ? 'Saving...' : 'Save'}
            </button>
          </ModalFooter>
          </form>
        </Modal>
      )}

      {/* ---- Notification History ---- */}
      <div className="mt-6 bg-white rounded-lg shadow">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-base font-semibold text-gray-900">
            Notification History
            <span className="ml-2 text-xs font-normal text-gray-500">({notifications.length})</span>
          </h2>
          {client.contact_email && (
            <button
              onClick={() => setShowSendMessage(true)}
              className="tap text-sm text-primary-600 hover:underline"
            >
              Send Now
            </button>
          )}
        </div>

        {!client.contact_email && (
          <p className="px-5 py-3 text-sm text-gray-500">No contact email on file — cannot send notifications.</p>
        )}

        <div className="divide-y divide-gray-100">
          {loadingNotifs ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            </div>
          ) : notifications.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-6">No notifications sent yet.</p>
          ) : (
            notifications.map((n) => (
              <div key={n.id} className="px-5 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${
                        n.status === 'sent'
                          ? 'bg-green-100 text-green-700'
                          : n.status === 'failed'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {n.status}
                    </span>
                    <span className="text-gray-900 font-medium">
                      {n.notification_type === 'reminder'
                        ? `Reminder (${n.days_offset}d before)`
                        : n.notification_type === 'overdue'
                        ? `Overdue (${n.days_offset}d after)`
                        : 'Custom message'}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {formatLocalDateTime(n.sent_at)}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  To: {n.recipient_email}
                  {n.call_sign && <> · {n.call_sign}</>}
                  {n.license_expiration && <> · exp {n.license_expiration}</>}
                </div>
                {n.error_message && (
                  <p className="text-xs text-red-500 mt-0.5">{n.error_message}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ---- Scheduled Messages ----
          Directly below the history, because the two answer the same question at
          different ends of time: what went out, and what is about to. */}
      {client.contact_email && (
        <ScheduledMessagesPanel
          clientId={id}
          refreshKey={scheduledRefresh}
          action={
            <button
              onClick={() => { setSendTemplateId(null); setShowSendMessage(true) }}
              className="tap text-sm text-primary-600 hover:underline"
            >
              Schedule a message
            </button>
          }
        />
      )}
    </div>

    {showSendCard && (
      <SendCardModal
        onClose={() => setShowSendCard(false)}
        clientId={id}
        defaultEmail={client?.contact_email ?? ''}
        onSent={() => setShowSendCard(false)}
      />
    )}
    {showSendMessage && (
      <SendMessageModal
        onClose={() => setShowSendMessage(false)}
        clientId={id}
        defaultEmail={client?.contact_email ?? ''}
        defaultTemplateId={sendTemplateId}
        onSent={() => {
          loadNotifications()
          setScheduledRefresh((n) => n + 1)
        }}
      />
    )}
    </>
  )
}

// ---------------------------------------------------------------------------
// TaskRow helper
// ---------------------------------------------------------------------------

function TaskRow({
  task,
  priorityColors,
  statusColors,
  onComplete,
  onDelete,
  onEdit,
}: {
  task: Task
  priorityColors: Record<TaskPriority, string>
  statusColors: Record<TaskStatus, string>
  onComplete: () => void
  onDelete: () => void
  onEdit: () => void
}) {
  const isOverdue =
    (task.due_at || task.due_date) &&
    task.status !== 'completed' &&
    task.status !== 'cancelled' &&
    new Date(task.due_at || task.due_date || '') < new Date()

  function formatTaskDue() {
    if (task.due_at) return formatLocalDateTime(task.due_at)
    if (task.due_date) return formatLocalDate(task.due_date)
    return null
  }

  const dueLabel = formatTaskDue()

  return (
    <div className={`px-5 py-3 group ${isOverdue ? 'bg-red-50' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {task.status !== 'completed' && task.status !== 'cancelled' ? (
            <button
              onClick={onComplete}
              title="Mark complete"
              className="flex-shrink-0 w-4 h-4 rounded border border-gray-300 hover:border-green-500 hover:bg-green-50"
            />
          ) : (
            <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-green-600 text-xs">✓</span>
          )}
          <div className="min-w-0">
            <button
              onClick={onEdit}
              className={`text-sm font-medium truncate text-left hover:text-primary-600 ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-900'}`}
              title="Edit task"
            >
              {task.title}
            </button>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <Badge label={task.priority} cls={priorityColors[task.priority]} />
              <Badge
                label={task.status.replace('_', ' ')}
                cls={statusColors[task.status]}
              />
              {dueLabel && (
                <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                  {isOverdue ? 'Overdue · ' : 'Due '}
                  {dueLabel}
                </span>
              )}
              {task.assigned_to && (
                <span className="text-xs text-gray-400">
                  → {task.assigned_to.full_name || task.assigned_to.email}
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 rounded"
          title="Delete task"
          aria-label="Delete task"
        >
          <XIcon />
        </button>
      </div>
    </div>
  )
}
